<template>
	<v-row>
		<v-col>
			<v-form v-model="formIsValid.manager" ref="managerForm">

				<ui-box :form="$refs.managerForm" :eager="!!localPartner.id">

					<v-row>
						<v-col>
							<h5>Account Management</h5>
							<span class="grey--text text--darken-1 text-body-2">
								Select partner status
							</span>
						</v-col>
					</v-row>

					<v-divider class="mb-10 mt-4 opacity-80"></v-divider>

					<v-row>
						 
						<v-col>
							<v-select
									v-model="localPartner.status"
									class="rounded-lg required"
									clearable
									hide-details="auto"
									:items="statuses"
									item-value="id"
									item-text="name"
									label="Status"
									outlined
									:rules="rules.status"
							></v-select>
						</v-col>
					</v-row>

				</ui-box>

			</v-form>

			<v-form v-model="formIsValid.org" ref="orgForm">

				<ui-box :form="$refs.orgForm" :eager="!!localPartner.id">

					<v-row>
						<v-col>
							<h5>Organization Information</h5>
						</v-col>
					</v-row>

					<v-divider class="mb-10 mt-4 opacity-80"></v-divider>

					<v-row>
						<v-col>
							<v-text-field
									v-model="localPartner.name"
									class="rounded-lg required"
									hide-details="auto"
									label="Name"
									outlined
									persistent-hint
									:rules="rules.name"
							></v-text-field>
						</v-col>
						<v-col>
							<v-text-field
									v-model="localPartner.name_legal"
									class="rounded-lg required"
									hide-details="auto"
									label="Legal Name"
									outlined
									persistent-hint
									:rules="rules.name"
							></v-text-field>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-select
									v-model="localPartner.partner_type_id"
									class="rounded-lg required"
									clearable
									hide-details="auto"
									:items="partnerTypes"
									item-value="id"
									item-text="name"
									label="Organization Type"
									outlined
									:rules="rules.org_type"
							></v-select>
						</v-col>
						<v-col>
							<v-select
									v-model="localPartner.partner_contract_type_id"
									class="rounded-lg required"
									clearable
									hide-details="auto"
									:items="partnerContractTypes"
									item-value="id"
									item-text="name"
									label="Contract Type"
									outlined
									:rules="rules.contract_type"
							></v-select>
						</v-col>
					</v-row>
					
				</ui-box>

			</v-form>

			<v-form v-model="formIsValid.basic" ref="basicForm">

				<ui-box
						:form="$refs.basicForm"
						:eager="!!localPartner.id"
				>

					<form-title title="Basic Details"/>

					<form-divider inner/>

					<v-row>

						<v-col cols="12" md="6">
							<v-text-field
									v-model="localPartner.address"
									class="rounded-lg required"
									hide-details="auto"
									id="street-address"
									label="Street Address"
									ref="autocomplete"
									outlined
									:rules="rules.address"
							></v-text-field>
						</v-col>

						<v-col cols="12" sm="6">
							<v-text-field
									v-model="localPartner.address_line_2"
									class="rounded-lg"
									hide-details="auto"
									label="Apt/Suite #"
									outlined
							></v-text-field>
						</v-col>

						<v-col cols="12" sm="6">
							<v-text-field
									v-model="localPartner.city"
									class="rounded-lg required"
									hide-details="auto"
									label="City"
									outlined
									:rules="rules.city"
							></v-text-field>
						</v-col>

						<v-col cols="12" sm="6">
							<v-text-field
									v-model="localPartner.state"
									class="rounded-lg required"
									hide-details="auto"
									label="State/Province"
									outlined
									:rules="rules.state"
							></v-text-field>
						</v-col>

						<v-col cols="12" sm="6">
							<v-text-field
									v-model="localPartner.zip_code"
									class="rounded-lg required"
									hide-details="auto"
									label="Zip/Postal Code"
									outlined
									:rules="rules.zip_code"
							></v-text-field>
						</v-col>

						<v-col cols="12" sm="6" md="3">
							<v-select
									v-model="localPartner.country"
									class="rounded-lg required"
									hide-details="auto"
									:items="countries"
									item-value="id"
									item-text="name"
									label="Country"
									outlined
									:rules="rules.country"
							></v-select>
						</v-col>

						<v-col cols="12" sm="6">
							<v-text-field
									v-model="localPartner.website"
									class="rounded-lg required"
									hide-details="auto"
									label="Website"
									outlined
									:rules="rules.website"
							></v-text-field>
						</v-col>

						<v-col cols="12" sm="6">
							<vue-tel-input-vuetify
									ref="phone"
									v-model="localPartner.phone"
									append-icon="mdi-menu-down"
									class="rounded-lg required"
									clearable
									:defaultCountry="localPartner.country"
									hide-details="auto"
									label="Phone Number"
									mode="international"
									outlined
									:preferred-countries="['us', 'ca']"
									:rules="rules.phone"
									@input="phoneInputEvent"
									@keydown="phoneKeyEvent"
							></vue-tel-input-vuetify>
						</v-col>
					</v-row>

				</ui-box>

			</v-form>

			<v-form v-model="formIsValid.advanced" ref="advancedForm">

				<ui-box
						:form="$refs.advancedForm"
						:eager="!!localPartner.id"
				>

					<form-title title="Advanced Details"/>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" sm="6" md="4">
							<v-select
									v-model="localPartner.timezone"
									class="rounded-lg required"
									hide-details="auto"
									:items="timezones"
									item-value="id"
									item-text="name"
									label="Timezone"
									:rules="rules.timezone"
									outlined
							></v-select>
						</v-col>
						<v-col cols="12" sm="6" md="4">
							<v-select
									v-model="localPartner.distance_unit"
									class="rounded-lg required"
									hide-details="auto"
									:items="distanceUnits"
									item-value="id"
									item-text="name"
									label="Distance Unit"
									:rules="rules.distance_unit"
									outlined
							></v-select>
						</v-col>
						<v-col cols="12" sm="6" md="4">
							<v-select
									v-model="localPartner.currency_id"
									class="rounded-lg required"
									hide-details="auto"
									:items="currencies"
									item-value="id"
									item-text="name"
									label="Currency"
									:rules="rules.currency_id"
									outlined
							></v-select>
						</v-col>
					</v-row>
				 
				</ui-box>

			</v-form>
			
			<v-form
					v-model="formIsValid.pay"
					:class="{ 'mb-8': partner.id }"
					ref="payForm"
			>

				<ui-box :form="$refs.payForm" :eager="!!localPartner.id">

					<v-row>
						<v-col>
							<h5>Payment Information</h5>
							<span class="grey--text text--darken-1 text-body-2">
								The following information will determine how payments are processed for this 
								partner.
							</span>
						</v-col>
					</v-row>

					<v-divider class="mb-10 mt-4 opacity-80"></v-divider>

					<v-row>
						<v-col>
							<v-select
									v-model="localPartner.payment_gateway"
									@change="paymentGatewayChanged"
									class="rounded-lg required"
									clearable
									hide-details="auto"
									:items="gateways"
									item-value="id"
									item-text="name"
									label="Gateway"
									:disabled="!!localPartner.id"
									outlined
									:rules="rules.gateway"
							></v-select>
						</v-col>
						<v-col>
							<v-select
									v-model="localPartner.partner_fee_structure_id"
									class="rounded-lg"
									clearable
									hide-details="auto"
									:items="feeStructures"
									item-value="id"
									item-text="name"
									label="Fee Structure"
									outlined
							></v-select>
						</v-col>
					</v-row>
					<v-row v-if="localPartner.id">
						<v-col>

							<v-alert
									border="right"
									class="my-0"
									colored-border
									:type="paymentGatewayAlertType"
									elevation="2"
							>
								<div class="align-center d-flex">
									<div class="flex-grow-1 me-8">
										<h6 v-if="paymentMethodId">
											Connected to {{ paymentGateway.name }}
										</h6>
										<h6 v-else>
											Connect to {{ paymentGateway.name }}
										</h6>
										<template v-if="paymentMethodId">
											Your {{ paymentGateway.name }} account is already connected and
											ready
											to go!
										</template>
										<template v-else>
											Click the connect button to finish setting up your payment gateway.
										</template>
									</div>
									<div v-if="!paymentMethodId" class="pe-6">
										<a :href="localPartner.paymentGatewayConnectUrl" target="_blank">
											<img
													:src="paymentGateway.image"
													height="30"
											/>
										</a>
									</div>
								</div>
							</v-alert>

						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-text-field
									v-model.number="localPartner.annual_contract_value"
									class="rounded-lg"
									hide-details="auto"
									label="Annual Contract"
									outlined
									persistent-hint
									prefix="$ "
									type="number"
									@wheel.native.prevent
							></v-text-field>
						</v-col>
						<v-col>
							<v-text-field
									v-model.number="localPartner.initial_contract_value"
									class="rounded-lg"
									hide-details="auto"
									label="Initial Contract"
									outlined
									persistent-hint
									prefix="$ "
									type="number"
									@wheel.native.prevent
							></v-text-field>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-dialog
									ref="signedDateDialog"
									v-model="signedDateModal"
									persistent
									:return-value.sync="localPartner.signed_at"
									width="290px"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
											v-model="localPartner.signed_at"
											v-bind="attrs"
											append-icon="mdi-calendar"
											class="rounded-lg"
											clearable
											:dense="$vuetify.breakpoint.smAndDown"
											hide-details="auto"
											label="Contract Signature Date"
											outlined
											readonly
											v-on="on"
									></v-text-field>
								</template>
								<v-date-picker
										v-model="localPartner.signed_at"
										no-title
								>
									<v-spacer></v-spacer>
									<v-btn color="primary" text @click="signedDateModal = false">Cancel
									</v-btn>
									<v-btn
											color="primary"
											text
											@click="$refs.signedDateDialog.save(localPartner.signed_at)"
									>
										OK
									</v-btn>
								</v-date-picker>
							</v-dialog>
						</v-col>
						<v-col>
							<v-dialog
									ref="renewalDateDialog"
									v-model="renewalDateModal"
									persistent
									:return-value.sync="localPartner.contract_renewal_date"
									width="290px"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
											v-model="localPartner.contract_renewal_date"
											v-bind="attrs"
											append-icon="mdi-calendar"
											class="rounded-lg"
											clearable
											:dense="$vuetify.breakpoint.smAndDown"
											hide-details="auto"
											label="Renewal Date"
											outlined
											readonly
											v-on="on"
									></v-text-field>
								</template>
								<v-date-picker
										v-model="localPartner.contract_renewal_date"
										no-title
								>
									<v-spacer></v-spacer>
									<v-btn color="primary" text @click="renewalDateModal = false">Cancel
									</v-btn>
									<v-btn
											color="primary"
											text
											@click="$refs.renewalDateDialog.save(localPartner.contract_renewal_date)"
									>
										OK
									</v-btn>
								</v-date-picker>
							</v-dialog>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-select
									v-model="localPartner.creative_services"
									class="rounded-lg"
									clearable
									hide-details="auto"
									:items="yesNo"
									item-value="id"
									item-text="name"
									label="Bandwango Creative Services"
									outlined
							></v-select>
						</v-col>
						<v-col>
							<span>&nbsp;</span>
						</v-col>
					</v-row>

				</ui-box>

			</v-form>

			<v-form
					v-if="!partner.id"
					v-model="formIsValid.user"
					ref="userForm"
					class="mb-8"
			>

				<ui-box :form="$refs.userForm" :eager="!!localPartner.id">

					<v-row>
						<v-col>
							<h5>Primary Contact - Optional</h5>
						</v-col>
					</v-row>

					<v-alert
							border="right"
							class="mt-4"
							:class="{
								'mb-10': showAddUserForm,
								'mb-2': !showAddUserForm
							}"
							colored-border
							type="error"
							elevation="2"
					>
						<div class="d-flex">
							<div class="error--text flex-grow-1 me-8">
								If you choose to enter a contact for this account now an email invitation
								will be sent immediately to the user. If you are provisioning this partner
								account to get a head start or build a demo pass <strong
									class="error--text text--darken-2">DO NOT</strong>
								create a user here.
							</div>
							<div class="pe-6">
								<template v-if="showAddUserForm">
									<v-btn
											color="grey darken-1"
											depressed
											outlined
											rounded
											small
											@click="cancelAddUser"
									>
										Cancel
									</v-btn>
								</template>
								<template v-else>
									<v-btn
											color="success"
											depressed
											rounded
											small
											@click="addUser"
									>
										Add User
									</v-btn>
								</template>
							</div>
						</div>
					</v-alert>

					<template v-if="showAddUserForm">

						<v-row>
							<v-col>
								<v-text-field
										v-model="localPartner.primaryContact.first_name"
										ref="first_name"
										class="rounded-lg"
										hide-details="auto"
										label="First Name"
										outlined
										:rules="rules.user.first_name"
								></v-text-field>
							</v-col>
							<v-col>
								<v-text-field
										v-model="localPartner.primaryContact.last_name"
										ref="last_name"
										class="rounded-lg"
										hide-details="auto"
										label="Last Name"
										outlined
										:rules="rules.user.last_name"
								></v-text-field>
							</v-col>
						</v-row>
						<v-row>
							<v-col cols="6">
								<v-text-field
										v-model="localPartner.primaryContact.email"
										ref="email"
										class="rounded-lg"
										hide-details="auto"
										label="Email"
										outlined
										type="email"
										:rules="rules.user.email"
								></v-text-field>
							</v-col>
						</v-row>

					</template>

				</ui-box>
			</v-form>
 
			<v-form v-model="formIsValid.additional" ref="additionalForm">
				<ui-box :form="$refs.additionalForm" :eager="!!localPartner.id">
					<form-title title="Additional Information"/>
					<form-divider inner/>

					<v-row>
						<v-col cols="12" sm="6">
							<v-select
									v-model="localPartner.auto_renew"
									class="rounded-lg"
									hide-details="auto"
									:items="yesNoBoolean"
									item-value="id"
									item-text="name"
									label="Auto-renew"
									outlined
									:rules="rules.auto_renew"
							></v-select>
						</v-col>
						<v-col cols="12" sm="6">
							<v-select
									v-model="localPartner.managed_sms"
									class="rounded-lg"
									hide-details="auto"
									:items="yesNoBoolean"
									item-value="id"
									item-text="name"
									label="Managed Text Message Service"
									outlined
									:rules="rules.managed_text_service"
							></v-select>
						</v-col>
					</v-row>

					<v-row>
						<v-col cols="12" sm="6">
							<v-select
									v-model="localPartner.charge_sms"
									class="rounded-lg"
									:disabled="isChargeSmsLocked"
									hide-details="auto"
									:items="yesNoBoolean"
									item-value="id"
									item-text="name"
									label="Charge for SMS"
									outlined
									:rules="rules.charge_for_sms"
							></v-select>
						</v-col>
						<v-col cols="12" sm="6">
							<v-select
									v-model="localPartner.dxe_connect_flat_rate"
									class="rounded-lg"
									hide-details="auto"
									:items="yesNoBoolean"
									item-value="id"
									item-text="name"
									label="Enrolled in DXE Connect"
									outlined
									:rules="rules.enrolled_in_dxe"
							></v-select>
						</v-col>
					</v-row>

					<v-row>
						<v-col cols="12" sm="6">
							<v-select
									v-model="localPartner.send_payments"
									class="rounded-lg"
									:disabled="localPartner.payment_gateway === 'self-stripe'"
									hide-details="auto"
									:items="yesNo"
									item-value="id"
									item-text="name"
									label="Bandwango Pays Merchants"
									outlined
									:rules="rules.bandwango_pays_merchants"
							></v-select>
						</v-col>
						<v-col cols="12" sm="6">
							<v-text-field
									v-model="localPartner.merchant_notes"
									class="rounded-lg"
									hide-details="auto"
									label="Payout Notes"
									outlined
							></v-text-field>
						</v-col>
					</v-row>

					<v-row>
						<v-col cols="12" sm="6">
							<v-select
									v-model="localPartner.special_features"
									class="rounded-lg"
									hide-details="auto"
									:items="yesNoBoolean"
									item-value="id"
									item-text="name"
									label="Special Features"
									outlined
									:rules="rules.special_features"
							></v-select>
						</v-col>
						<v-col cols="12" sm="6">
							<v-select
									v-model="localPartner.contract_modification"
									class="rounded-lg"
									hide-details="auto"
									:items="yesNoBoolean"
									item-value="id"
									item-text="name"
									label="Contract modification"
									outlined
									:rules="rules.contract_modification"
							></v-select>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<v-form v-model="formIsValid.legal" ref="legalForm">
				<ui-box
					:form="$refs.legalForm"
					:eager="!!localPartner.id">

					<form-title title="Legal Information and Policies"/>
					

					<form-divider inner/>
					
					<v-row>
						<v-col>
							<v-textarea
								v-model="localPartner.terms_conditions"
								auto-grow
								class="rounded-lg"
								label="Terms & Conditions"
								outlined
								rows="10"
							></v-textarea>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-textarea
								v-model="localPartner.privacy_policy"
								auto-grow
								class="rounded-lg"
								label="Privacy Policy"
								outlined
								rows="4"
							></v-textarea>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-textarea
								v-model="localPartner.refund_policy"
								auto-grow
								class="rounded-lg"
								label="Refund Policy"
								outlined
								rows="10"
							></v-textarea>
						</v-col>
					</v-row>
				</ui-box>




			</v-form>
			
			
			
			<save-form
					:refs="$refs"
					:is-saving="isSaving"
					save-text="Save & Preview"
					@cancel-action="cancelChanges"
					@save-action="saveChanges"
			></save-form>

		</v-col>
	</v-row>
</template>

<script>
export default {
	name: "EditPartner",
	props: {
		partner: {
			type: Object,
			default: () => ({ primaryContact: {} })
		},
		partnerTypes: {
			type: Array,
			default: () => []
		},
		partnerContractTypes: {
			type: Array,
			default: () => []
		},
		feeStructures: {
			type: Array,
			default: () => []
		},
		showSections: {
			type: Array,
			default: () => []
		},
		template: {
			type: Object,
			default: () => ({})
		},
		returnUrl: {
			type: String,
			default: ''
		},
		adminUsers: {
			type: Array,
			default: () => []
		},
	},
	data() {
		return {
			countries: [
				{ id: "US", name: "United States" },
				{ id: "CA", name: "Canada" },
				{ id: "GB", name: "United Kingdom" },
			],
			currencies: [
				{ id: 1, name: "USD - $" },
				{ id: 2, name: "CAD - C$" },
				{ id: 3, name: "EUR - €" },
				{ id: 4, name: "GBP - £" },
			], 
			distanceUnits: [
				{ id: "mi", name: "Miles - mi." },
				{ id: "km", name: "Kilometers - km." },
			],
			formIsValid: {
				manager: false,
				org: false,
				pay: false,
				user: false,
				additional: false
			},
			gateways: [
				{ id: 'bandwango-stripe', name: "Bandwango Stripe" },
				{ id: 'self-stripe', name: "Partner Stripe" },
			],
			isSaving: false,
			localPartner: {
				...this.partner,
				primaryContact: {
					email: '',
					first_name: '',
					last_name: '',
					...this.partner.primaryContact
				},
			},
			phoneIsValid: true,
			renewalDateModal: false,
			rules: {
				address: [v => !!v || 'Street Address is required.'],
				city: [v => !!v || 'City is required.'],
				contract_type: [v => !!v || 'Contract type is required.'],
				currency_id: [v => !!v || 'Currency is required.'],
				distance_unit: [v => !!v || 'Distance unit is required.'],
				gateway: [v => !!v || 'Gateway is required.'],
				country: [v => !!v || 'Country is required.'],
				name: [v => !!v || 'Partner name is required.'],
				org_type: [v => !!v || 'Organization type is required.'],
				phone: [
					() => this.phoneIsValid || 'Please enter a valid phone.'
				],
				status: [v => !!v || 'Status is required.'],
				state: [v => !!v || 'State is required.'],
				timezone: [v => !!v || 'Timezone is required.'],
				user: {
					email: [
						v => !!v || 'Email is required.',
						v => {
							const pattern =
									// eslint-disable-next-line max-len
									/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
							return pattern.test(v) || 'Please enter a valid e-mail.';
						}
					],
					firstName: [v => !!v || 'First name is required.'],
					lastName: [v => !!v || 'Last name is required.']
				},
				website: [
					v => {
						const protocolRegex = /^https?:\/\//;
						return protocolRegex.test(v) || 'Please include the whole website url including http(s)://'
					},
					v => {
						const fullRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-a-zA-Z0-9()@:%_+.~#?&\/=]*$/;
						return fullRegex.test(v) || 'Website url is invalid.'
					}
				],
				zip_code: [v => !!v || 'Zip/Postal Code is required.'],
				auto_renew: [v => v !== null || 'Auto-renew is required.'],
				managed_text_service: [v => v !== null || 'Managed Text Message Service is required.'],
				charge_for_sms: [v => v !== null || 'Charge for SMS is required.'],
				enrolled_in_dxe: [v => v !== null || 'Enrolled in DXE Connect is required.'],
				bandwango_pays_merchants: [v => v !== null || 'Bandwango Pays Merchants is required.'],
				special_features: [v => v !== null || 'Special Features is required.'],
				contract_modification: [v => v !== null || 'Contract modification is required.'],

			},
			showAddUserForm: false,
			signedDateModal: false,
			statuses: [
				{ id: "active", name: "Active" },
				{ id: "inactive", name: "Inactive" },
				{ id: "test", name: "Test" },
			],
			timezones: [
				{ id: "America/Anchorage", name: "Alaska Daylight Time" },
				{ id: "America/Los_Angeles", name: "Pacific Daylight Time" },
				{ id: "America/Phoenix", name: "Arizona" },
				{ id: "America/Denver", name: "Mountain Daylight Time" },
				{ id: "America/Chicago", name: "Central Daylight Time" },
				{ id: "America/New_York", name: "Eastern Daylight Time" },
				{ id: "Atlantic/Bermuda", name: "Atlantic Bermuda" },
				{ id: "Europe/Madrid", name: "Central European Time" },
				{ id: "Europe/Athens", name: "Eastern European Time" },
				{ id: "Europe/London", name: "Western European Time" },
			],
			yesNo: [
				{ id: 1, name: "Yes" },
				{ id: 0, name: "No" },
			],
			yesNoBoolean: [
				{ id: true, name: "Yes" },
				{ id: false, name: "No" },
			]
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.setUpAutoComplete();
			this.fixAddressPlaceholder();
		});
	},
	computed: {
		paymentGatewayAlertType() {
			return this.paymentMethodId ? 'success' : 'error';
		},
		paymentGateway() {
			switch (this.localPartner.partner_payment_method?.payment_gateway_id
			|| this.localPartner.payment_gateway_id) {
				case 1:
					return {
						name: 'Braintree',
						image: '/images/connect-braintree.png',
					};
				case 2:
					return {
						name: 'Stripe',
						image: '/images/light-on-light@3x.png',
					};
				default:
					return {
						name: '',
						image: '',
					};
			}
		},
		paymentMethodId() {
			return this.localPartner.partner_payment_method_id || null;
		},
		successSpecialist: {
			get() {
				return (this.localPartner.managers || [])[0];
			},
			set(manager) {
				this.localPartner.managers = [manager];
			}
		},
		isChargeSmsLocked() {
			return this.localPartner.managed_sms === true || this.localPartner.managed_sms === 1;
		}
	},
	watch: {
		'localPartner.managed_sms'(val) {
			if (val === true || val === 1) {
				this.localPartner.charge_sms = false;
			}
		},
	},
	methods: {
		setUpAutoComplete() {

			const options = {
				fields: [
					'name',
					'geometry',
					'place_id',
					'address_components',
					'formatted_address',
				],
			};

			this.autocomplete = new google.maps.places.Autocomplete(this.$refs.autocomplete.$el.querySelector('input'), options);
			this.autocomplete.addListener("place_changed", () => {
				const place = this.autocomplete.getPlace();
				let street, street_number;
				place.address_components.forEach(c => {
					if(c.types.includes('route')) {
						street = c.short_name;
					}
					if(c.types.includes('street_number')) {
						street_number = c.short_name;
					}
					if(c.types.includes('subpremise')) {
						this.localPartner.address_line_2 = c.long_name;
					}
					if(c.types.includes('postal_code')) {
						this.localPartner.zip_code = c.long_name;
					}
					if(c.types.includes('administrative_area_level_1')) {
						this.localPartner.state = c.long_name;
					}
					if(c.types.includes('locality')) {
						this.localPartner.city = c.long_name;
					}
					if(c.types.includes('country')) {
						this.localPartner.country = c.short_name;
					}
				})
				if(street_number !== undefined && street !== undefined) {
					this.localPartner.address = `${street_number} ${street}`;
				}
				this.localPartner.latitude = place.geometry.location.lat();
				this.localPartner.longitude = place.geometry.location.lng();
				this.localPartner.google_place_id = place.place_id;
				this.localPartner.address_components = place.address_components;
			});

		},
		fixAddressPlaceholder() {
			const $this = this;
			setTimeout(function () {
				const inputElement = document.getElementById('street-address');
				if(inputElement?.placeholder) {
					inputElement.placeholder = '';
				}
				else {
					$this.fixAddressPlaceholder();
				}
			}, 5);
		},
		addUser() {
			this.showAddUserForm = true;
		},
		cancelAddUser() {
			this.showAddUserForm = false;
			this.localPartner.primaryContact.email = null;
			this.localPartner.primaryContact.first_name = null;
			this.localPartner.primaryContact.last_name = null;
		},
		cancelChanges() {
			window.location = this.getReturnUrl();
		},
		getEditUrl() {
			return `/super-admin-portal/partners/main/edit-partner/${this.localPartner.id}`;
		},
		getReturnUrl() {
			return `/super-admin-portal/partners/main/list-partners`;
		},
		saveChanges() {
			this.isSaving = true;
			const body = { ...this.localPartner };
			if(body?.managers) {
				body.managers = body.managers.map(x => x.id ? x.id : x);
			}
			let url = `/super-admin-portal/partners/main/save-partner/${this.localPartner.id || ''}`;
			axios.put(url, body)
					.then(response => {
						this.localPartner.id = response.data.partner.id;
						window.EventBus.$emit('system-messages', [
							{
								message: this.localPartner.name + ' ' + (this.localPartner.id
										? 'updated'
										: 'created') + ' - redirecting now...',
								messageType: 'success',
								showOverlay: true
							}
						]);
						setTimeout(() => {
							window.location = this.getReturnUrl();
						}, 4000);
					})
					.catch(error => {
						this.isSaving = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},
		shouldShowSection(section) {
			return !this.showSections.length || this.showSections.includes(section);
		},
		paymentGatewayChanged() {
			if(this.localPartner.payment_gateway === 'self-stripe') {
				this.localPartner.send_payments = 0;
			}
		},
		phoneInputEvent(formattedNumber, { valid }) {
			// Only flip the form validity to false if NOT valid, do nothing on valid = true
			// if(formattedNumber && !valid) this.formIsValid = false;
			// For field validation rule
			this.phoneIsValid = valid;
		},
		phoneKeyEvent(e) {
			const validCharsRegex = new RegExp(/^[0-9 +\-()]*$/);

			// Allow Control+V or Command+V for pasting
			const isPasteOperation = (e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey);

			if(!validCharsRegex.test(e.key) && ![
				'ArrowLeft',
				'ArrowRight',
				'Backspace',
				'Delete',
				'Tab'
			].includes(e.key) && !isPasteOperation) {
				e.preventDefault();
			}
		},
	}
};
</script>

<style lang="scss">
@use 'sass:color';

.wiz-btn-icon {
	background-color: var(--v-info-darken1) !important;
	border-radius: 50% !important;
	color: white !important;
	font-size: 28px !important;
	margin: 0 0.75rem 0 -1.5rem !important;
	padding: 0.75rem !important;
}
</style>




